import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  HStack,
} from "@chakra-ui/react";
import SectionHeader from "./SectionHeader";

type OrbitParams = {
  a: number;
  e: number;
  raan: number;
  i: number;
};

type Satellite = {
  id: number;
  color: string;
  f: number;
  x: number;
  y: number;
  pending: OrbitParams;
};

// The stage geometry is shared by the animation loop and the Earth marker, so
// both have to agree on where the focus sits.
const MOBILE_BREAKPOINT = 768;
const CENTER = {
  mobile: { x: 0.5, y: 0.32 },
  desktop: { x: 0.75, y: 0.65 },
};
// On a phone the control panel is overlaid across the bottom of the stage; keep
// the orbits above it.
const MOBILE_PANEL_RESERVE = 250;
export default function SatelliteApp() {
  const size = 6;

  const [a, setA] = useState(200);
  const [e, setE] = useState(0.4);
  const [raan, setRaan] = useState(0);
  const [i, setI] = useState(0);

  const [selectedId, setSelectedId] = useState(0);

  const [satellites, setSatellites] = useState<Satellite[]>([
    {
      id: 0,
      color: "red",
      f: 0,
      x: 0,
      y: 0,
      pending: { a: 200, e: 0.4, raan: 0, i: 0 },
    },
  ]);

  const requestRef = useRef<number | null>(null);
  const idRef = useRef(1);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const colors = [
    "red",
    "lime",
    "cyan",
    "yellow",
    "orange",
    "magenta",
    "white",
  ];

  // 🧠 animation loop
  useEffect(() => {
    const animate = () => {
      const stage = stageRef.current;
      if (!stage) {
        requestRef.current = requestAnimationFrame(animate);
        return;
      }

      const width = stage.clientWidth;
      const height = stage.clientHeight;
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      const center = isMobile ? CENTER.mobile : CENTER.desktop;
      const cx = width * center.x;
      const cy = height * center.y;

      // Distance from the focus to the nearest edge of the usable stage.
      const floor = isMobile ? height - MOBILE_PANEL_RESERVE : height;
      const halfExtent = Math.min(cx, width - cx, cy, floor - cy);

      setSatellites((prev) => {
        // Fit the widest orbit to the stage rather than a fixed factor, so the
        // sliders stay usable across their whole range on a narrow screen.
        const maxRadius = prev.reduce(
          (widest, sat) =>
            Math.max(widest, sat.pending.a * (1 + sat.pending.e)),
          1,
        );
        const scale = isMobile ? Math.min(1, halfExtent / maxRadius) : 1;

        return prev.map((sat) => {
          let f = sat.f;

          const { a, e, raan, i } = sat.pending;

          const r = (a * (1 - e * e)) / (1 + e * Math.cos(f));

          const cosF = Math.cos(f);
          const sinF = Math.sin(f);

          const cosR = Math.cos(raan);
          const sinR = Math.sin(raan);

          const cosI = Math.cos(i);

          const x = cx - size / 2 + (cosF * cosR - cosI * sinR * sinF) * r * scale;

          const y = cy - size / 2 + (cosF * sinR + cosI * cosR * sinF) * r * scale;

          const angularVelocity = 0.02 * Math.pow(a / r, 2);
          f += angularVelocity;
          
          if (f > Math.PI * 2) f -= Math.PI * 2;

          return { ...sat, f, x, y };
        });
      });

      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  // update selected satellite params
  useEffect(() => {
    setSatellites((prev) =>
      prev.map((sat) =>
        sat.id === selectedId ? { ...sat, pending: { a, e, raan, i } } : sat,
      ),
    );
  }, [a, e, raan, i, selectedId]);

  const addSatellite = () => {
    const id = idRef.current++;

    setSatellites((prev) => [
      ...prev,
      {
        id,
        color: colors[prev.length % colors.length],
        f: Math.random() * Math.PI * 2,
        x: 0,
        y: 0,
        pending: { a, e, raan, i },
      },
    ]);

    setSelectedId(id);
  };

  const selectSatellite = (id: number) => {
    setSelectedId(id);

    const sat = satellites.find((s) => s.id === id);
    if (!sat) return;

    setA(sat.pending.a);
    setE(sat.pending.e);
    setRaan(sat.pending.raan);
    setI(sat.pending.i);
  };

  return (
      <Box w="100%" bg="black" color="white" py={{ base: 20, md: 28 }}>
        <Container maxW="5xl" px={{ base: 6, md: 8 }}>
        <SectionHeader
          eyebrow="04 / Simulation"
          title="Orbital mechanics playground"
          description="Interactive multi-satellite visualization with adjustable Keplerian elements: semi-major axis, eccentricity, RAAN and inclination."
        />

        {/* Animation stage */}
        <Box
          ref={stageRef}
          position="relative"
          overflow="hidden"
          w="100%"
          h={{ base: "70vh", md: "80vh" }}
          borderRadius="md"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          {/* Earth */}
          <Box
            position="absolute"
            left={`${CENTER.mobile.x * 100}%`}
            top={`${CENTER.mobile.y * 100}%`}
            sx={{
              "@media (min-width: 48em)": {
                left: `${CENTER.desktop.x * 100}%`,
                top: `${CENTER.desktop.y * 100}%`,
              },
            }}
            w="14px"
            h="14px"
            borderRadius="50%"
            bg="white"
            transform="translate(-50%, -50%)"
            boxShadow="0 0 20px rgba(255,255,255,0.7)"
          />

          {/* Satellites */}
          {satellites.map((sat) => (
            <Box
              key={sat.id}
              position="absolute"
              left={sat.x}
              top={sat.y}
              w={`${size}px`}
              h={`${size}px`}
              borderRadius="50%"
              bg={sat.color}
              transition="box-shadow 0.2s ease"
              cursor="pointer"
              onClick={() => selectSatellite(sat.id)}
              boxShadow={
                sat.id === selectedId
                  ? `0 0 14px ${sat.color}`
                  : "0 0 8px rgba(255,255,255,0.15)"
              }
            />
          ))}

          {/* Control Panel */}
          <Box
            position="absolute"
            bottom={{ base: 4, md: 8 }}
            left={{ base: 4, md: 8 }}
            right={{ base: 4, md: "auto" }}
            w={{ base: "auto", md: "380px" }}
            p={{ base: 4, md: 6 }}
            bg="rgba(255,255,255,0.03)"
            backdropFilter="blur(12px)"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="md"
          >
          <VStack align="stretch" spacing={{ base: 3, md: 5 }}>
            <HStack justify="space-between">
              <Text
                fontFamily="mono"
                fontSize="xs"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="whiteAlpha.700"
              >
                Controls
              </Text>

              <Button
                size={{ base: "xs", md: "sm" }}
                variant="outline"
                color="whiteAlpha.800"
                borderColor="whiteAlpha.300"
                fontWeight="400"
                _hover={{
                  bg: "whiteAlpha.100",
                  borderColor: "whiteAlpha.600",
                  color: "white",
                }}
                onClick={addSatellite}
              >
                Add Satellite
              </Button>
            </HStack>

            <Box>
              <Text
                fontFamily="mono"
                fontSize="2xs"
                letterSpacing="0.12em"
                textTransform="uppercase"
                color="whiteAlpha.500"
                mb={2}
              >
                Semi-major Axis
              </Text>

              <Slider min={50} max={500} value={a} onChange={setA}>
                <SliderTrack bg="whiteAlpha.100">
                  <SliderFilledTrack bg="white" />
                </SliderTrack>
                <SliderThumb bg="white" />
              </Slider>
            </Box>

            <Box>
              <Text
                fontFamily="mono"
                fontSize="2xs"
                letterSpacing="0.12em"
                textTransform="uppercase"
                color="whiteAlpha.500"
                mb={2}
              >
                Eccentricity
              </Text>

              <Slider min={0} max={0.9} step={0.01} value={e} onChange={setE}>
                <SliderTrack bg="whiteAlpha.100">
                  <SliderFilledTrack bg="white" />
                </SliderTrack>
                <SliderThumb bg="white" />
              </Slider>
            </Box>

            <Box>
              <Text
                fontFamily="mono"
                fontSize="2xs"
                letterSpacing="0.12em"
                textTransform="uppercase"
                color="whiteAlpha.500"
                mb={2}
              >
                RAAN
              </Text>

              <Slider
                min={0}
                max={Math.PI * 2}
                step={0.01}
                value={raan}
                onChange={setRaan}
              >
                <SliderTrack bg="whiteAlpha.100">
                  <SliderFilledTrack bg="white" />
                </SliderTrack>
                <SliderThumb bg="white" />
              </Slider>
            </Box>

            <Box>
              <Text
                fontFamily="mono"
                fontSize="2xs"
                letterSpacing="0.12em"
                textTransform="uppercase"
                color="whiteAlpha.500"
                mb={2}
              >
                Inclination
              </Text>

              <Slider
                min={0}
                max={Math.PI / 2}
                step={0.01}
                value={i}
                onChange={setI}
              >
                <SliderTrack bg="whiteAlpha.100">
                  <SliderFilledTrack bg="white" />
                </SliderTrack>
                <SliderThumb bg="white" />
              </Slider>
            </Box>

            {/* Satellite Selection */}
            <HStack
              wrap="wrap"
              spacing={2}
              pt={2}
              maxH={{ base: "60px", md: "auto" }}
              overflowY="auto"
            >
              {satellites.map((sat) => (
                <Button
                  key={sat.id}
                  size="xs"
                  bg={sat.id === selectedId ? "white" : "whiteAlpha.100"}
                  color={sat.id === selectedId ? "black" : "white"}
                  borderRadius="full"
                  _hover={{
                    bg: sat.id === selectedId ? "white" : "whiteAlpha.200",
                  }}
                  onClick={() => selectSatellite(sat.id)}
                >
                  {sat.id}
                </Button>
              ))}
            </HStack>
          </VStack>
          </Box>
        </Box>
        </Container>
      </Box>
  );
}
